import { isEmpty } from 'ramda'

/**
 * Determines the best user location based on priority IP location > authedUser
 * address > policy address
 *
 * @param {Object} authedUser - Authenticated user object with address
 * @param {Object} currentLocation - Current location from browser
 * @param {Object} ipLocation - Location from IP
 * @param {Object} policyLocation - Location from policy
 * @param {Object} queryLocation - Location from query params
 * @param {Object} smartLocation - Location from smart layer
 * @returns {Object | null} The best available location or null if none found
 */
export const chooseBestUserLocation = ({
  authedUser = {},
  currentLocation = {},
  ipLocation = {},
  policyLocation = {},
  smartLocation = {},
  // using "queryLocation" instead of "urlLocation" to avoid
  // confusion with the actual URL and this value comes from
  // the query params (even tho technically it is in the URL).
  queryLocation = {},
}) => {
  // Define location sources in priority order. "Smart" is before
  // "query" because the user might have just done a search that
  // would override whatever might already be in the URL.
  const locationSources = [
    { source: 'smart', data: smartLocation },
    // using 'url' as the source to avoid confusion with 'query'
    // which is in the query params representing the query keywords.
    { source: 'url', data: queryLocation },
    // Current location is above user address because it is more accurate
    // but requires permission from the user as it's from the device.
    { source: 'current', data: currentLocation },
    // Mailing address is above policy address because member may have
    // moved since they got their policy.
    { source: 'user', data: authedUser?.mailingAddress },
    { source: 'policy', data: policyLocation },
    // IP is last resort because I might be on a VPN that is not
    // anywhere near where I actually am or live or want to sesarch.
    { source: 'ip', data: ipLocation },
  ]

  // Find the first valid location source
  for (const { source, data } of locationSources) {
    // if there's no data for this source, skip it
    const hasData = data && !isEmpty(data)
    if (!hasData) continue

    const hasCityState = data.city && data.state
    const hasCityOnly = data.city && !data.state && !data.zip

    // The smart layer could label these a couple of different ways
    // so we need to check for all the possible keys.
    const hasCoords =
      (data.lat || data.latitude) && (data.lon || data.longitude)
    const hasZip = data.zip || data.zipCode || data.postalCode

    if (hasCityOnly || hasCityState || hasCoords || hasZip) {
      return {
        city: data.city || undefined,
        state: data.state || undefined,
        zip: data.zip || data.zipCode || data.postalCode || undefined,
        lat: data.lat || data.latitude || undefined,
        lon: data.lon || data.longitude || undefined,
        // source only for debugging, not normally used in URL
        source,
      }
    }
  }

  // Return null if no valid location found
  return null
}
